import { useEffect, useState } from 'react';
import * as Location from 'expo-location';
import { DEFAULT_LOCATION, DefaultLocation } from '@/utils/location-defaults';

export type LocationStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable';

export interface UserLocation {
  status: LocationStatus;
  coords: {
    latitude: number;
    longitude: number;
  } | null;
  defaultLocation: DefaultLocation;
}

/**
 * Hook to get user's current location
 * Requests permission on mount and returns location status and coordinates
 * Falls back to default location (Bloomington, IN) if permission denied or unavailable
 *
 * @returns User location status and coordinates
 *
 * @example
 * ```typescript
 * const { status, coords, defaultLocation } = useUserLocation();
 *
 * if (status === 'granted' && coords) {
 *   // Use user's location
 * } else {
 *   // Use defaultLocation
 * }
 * ```
 */
export function useUserLocation(): UserLocation {
  const [status, setStatus] = useState<LocationStatus>('idle');
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function requestLocation() {
      try {
        setStatus('requesting');

        // Request location permissions
        const { status: permissionStatus } = await Location.requestForegroundPermissionsAsync();

        if (!isMounted) return;

        if (permissionStatus !== 'granted') {
          setStatus('denied');
          setCoords(null);
          return;
        }

        // Get current position
        const location = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });

        if (!isMounted) return;

        setStatus('granted');
        setCoords({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        });
      } catch (error) {
        if (!isMounted) return;

        console.warn('Failed to get user location:', error);
        setStatus('unavailable');
        setCoords(null);
      }
    }

    requestLocation();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    status,
    coords,
    defaultLocation: DEFAULT_LOCATION,
  };
}

